import { ArrowLeft, Send, Smile } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';

interface Message {
  id: string;
  type: 'sent' | 'received' | 'system';
  text: string;
  timestamp: string;
  icon?: string;
}

interface ChatConversationViewProps {
  userId: string; // User ID of the other user
  userName: string;
  userPhoto?: string;
  onBack: () => void;
}

export function ChatConversationView({ userId, userName, userPhoto, onBack }: ChatConversationViewProps) {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [chatMode, setChatMode] = useState<'auto' | 'manual' | null>(null);
  const [chatApproved, setChatApproved] = useState<boolean | null>(null);
  const [updatingApproval, setUpdatingApproval] = useState(false);

  const emojis = ['😀', '😂', '😍', '😎', '🤝', '🔥', '🎵', '🎧'];
  
  const formatTimestamp = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };
  
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        setAccessDenied(false);
        const response = await api.chat.getMessages(Number(userId), 50, 0);
        const messageList: Message[] = response.data.map((msg) => ({
          id: String(msg.id),
          type: msg.is_from_me ? 'sent' : 'received',
          text: msg.text,
          timestamp: formatTimestamp(msg.created_at),
        }));
        setMessages(messageList);
      } catch (error: any) {
        console.error('Failed to fetch messages:', error);
        const status = error?.response?.status;
        if (status === 403) {
          // Current user is not allowed to see this conversation
          setAccessDenied(true);
          setMessages([]);
        } else {
          setMessages([]);
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (userId) {
      fetchMessages();
    }
  }, [userId]);

  // Load per-chat approval status for the CURRENT user.
  useEffect(() => {
    const fetchChatApproval = async () => {
      try {
        setChatMode(null);
        setChatApproved(null);
        const status = await api.chat.getChatApproval(Number(userId));
        setChatMode(status.mode);
        setChatApproved(status.approved);
      } catch (error) {
        console.error('Failed to fetch chat approval status:', error);
      }
    };

    if (userId) {
      fetchChatApproval();
    }
  }, [userId]);
  
  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || sending || accessDenied) return;
    
    try {
      setSending(true);
      const response = await api.chat.sendMessage(Number(userId), text);
      setShowEmojiPicker(false);
      
      // Add the sent message to the list
      const newMessage: Message = {
        id: String(response.message.id),
        type: 'sent',
        text: response.message.text,
        timestamp: formatTimestamp(response.message.created_at),
      };
      setMessages((prev) => [...prev, newMessage]);
      setInputValue('');
    } catch (error: any) {
      console.error('Failed to send message:', error);
      const status = error?.response?.status;
      if (status === 403) {
        setAccessDenied(true);
        const detail = error?.response?.data?.detail;
        alert(detail || 'This user has not allowed chat with you yet.');
      } else {
        alert('Failed to send message. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  const handleAllowChat = async () => {
    if (chatMode !== 'manual' || chatApproved || updatingApproval) return;

    try {
      setUpdatingApproval(true);
      const result = await api.chat.setChatApproval(Number(userId), true);
      setChatApproved(result.approved);
      // Once approved, clear any previous access denied state.
      setAccessDenied(false);
    } catch (error) {
      console.error('Failed to allow chat for this user:', error);
      alert('Не вдалося дозволити чат. Спробуйте ще раз.');
    } finally {
      setUpdatingApproval(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-splyt-gradient relative">
      {/* Ambient background effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00FF88] opacity-10 blur-[120px] rounded-full" />
        <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-[#8B5CF6] opacity-10 blur-[120px] rounded-full" />
      </div>

      {/* Header */}
      <div
        className="relative z-10 flex items-center gap-3 px-4 pt-4 pb-3 border-b glass"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
        }}
      >
        <button
          onClick={onBack}
          className="w-10 h-10 flex items-center justify-center rounded-full border transition-all hover:border-white/30 glass"
        >
          <ArrowLeft className="w-5 h-5" style={{ color: '#00FF88' }} />
        </button>
        <div className="flex-1 flex items-center justify-between gap-3">
          <h1
            style={{
              color: '#FFFFFF',
              fontSize: '16px',
            }}
          >
            {userName}
          </h1>
          {chatMode === 'manual' && (
            <button
              type="button"
              onClick={handleAllowChat}
              disabled={chatApproved || updatingApproval}
              className="px-3 py-1.5 rounded-full border text-xs md:text-sm transition-all hover:border-[#00FF88]/60 disabled:opacity-60 disabled:cursor-default"
              style={{
                borderColor: chatApproved
                  ? 'rgba(0, 255, 136, 0.7)'
                  : 'rgba(255, 255, 255, 0.25)',
                background: chatApproved
                  ? 'rgba(0, 255, 136, 0.16)'
                  : 'rgba(255, 255, 255, 0.04)',
                color: '#FFFFFF',
              }}
            >
              {chatApproved
                ? 'Чат дозволено'
                : updatingApproval
                ? 'Збереження...'
                : 'Дозволити чат'}
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="relative z-10 flex-1 overflow-y-auto px-4 py-6 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
              Loading messages...
            </p>
          </div>
        ) : accessDenied ? (
          <div className="flex items-center justify-center p-8">
            <p
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                textAlign: 'center',
              }}
            >
              Ця людина поки що не дозволила чат з вами. Ви зможете писати,
              коли вона натисне «Дозволити чат».
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
              No messages yet. Start the conversation!
            </p>
          </div>
        ) : (
          messages.map((message) => {
          if (message.type === 'system') {
            return (
              <div key={message.id} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center border-2"
                     style={{
                       background: 'rgba(0, 255, 136, 0.2)',
                       borderColor: 'rgba(0, 255, 136, 0.5)',
                     }}>
                  <span style={{ fontSize: '20px' }}>{message.icon}</span>
                </div>
                <div className="flex-1">
                  <p style={{ color: '#FFFFFF', marginBottom: '4px' }}>
                    {message.text}
                  </p>
                  <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
                    {message.timestamp}
                  </p>
                </div>
              </div>
            );
          }

          if (message.type === 'sent') {
            return (
              <div key={message.id} className="flex justify-end">
                <div className="max-w-[75%]">
                  <div className="rounded-2xl px-4 py-3 relative"
                       style={{
                         background: '#00FF88',
                         color: '#0F1419',
                       }}>
                    <p style={{ fontSize: '14px' }}>{message.text}</p>
                  </div>
                  <p style={{ 
                    color: 'rgba(255, 255, 255, 0.5)', 
                    fontSize: '12px',
                    textAlign: 'right',
                    marginTop: '4px',
                    paddingRight: '8px',
                  }}>
                    {message.timestamp}
                  </p>
                </div>
              </div>
            );
          }

          if (message.type === 'received') {
            return (
              <div key={message.id} className="flex gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border overflow-hidden bg-gradient-to-br from-[#00FF88] to-[#8B5CF6]"
                     style={{
                       boxShadow: '0 4px 12px rgba(0, 255, 136, 0.2)',
                     }}>
                  {userPhoto ? (
                    <img src={userPhoto} alt={userName} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span style={{ color: '#FFFFFF', fontSize: '18px' }}>{message.icon || '👤'}</span>
                  )}
                </div>
                <div className="max-w-[65%]">
                  <div className="rounded-2xl px-4 py-3 glass"
                       style={{
                         color: '#FFFFFF',
                       }}>
                    <p style={{ fontSize: '14px' }}>{message.text}</p>
                  </div>
                  <p style={{ 
                    color: 'rgba(255, 255, 255, 0.5)', 
                    fontSize: '12px',
                    marginTop: '4px',
                    paddingLeft: '8px',
                  }}>
                    {message.timestamp}
                  </p>
                </div>
              </div>
            );
          }

          return null;
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Bar */}
      <div className="relative z-10 px-4 py-4 border-t glass"
           style={{
             borderColor: 'rgba(255, 255, 255, 0.1)',
           }}>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSend();
              }
            }}
            placeholder={
              accessDenied
                ? 'Чат недоступний, поки інша сторона не дозволить його'
                : userName
            }
            className="flex-1 px-4 py-3 rounded-full outline-none transition-all glass placeholder:text-white/50"
            style={{
              color: '#FFFFFF',
            }}
            readOnly={accessDenied}
          />

          <button
            type="button"
            onClick={() => setShowEmojiPicker((prev) => !prev)}
            className="w-10 h-10 flex items-center justify-center rounded-full border transition-all hover:border-white/30 glass"
          >
            <Smile className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </button>
          
          <button 
            onClick={handleSend}
            disabled={accessDenied || sending || !inputValue.trim()}
            className="w-10 h-10 flex items-center justify-center rounded-full transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: '#00FF88',
              boxShadow: '0 4px 12px rgba(0, 255, 136, 0.4)',
            }}
          >
            <Send className="w-5 h-5" style={{ color: '#0F1419' }} />
          </button>
        </div>
      </div>

      {showEmojiPicker && (
        <div
          className="absolute z-20 flex flex-wrap gap-2 px-3 py-2 rounded-2xl glass"
          style={{
            bottom: '88px',
            right: '20px',
            background: 'rgba(15, 20, 25, 0.95)',
          }}
        >
          {emojis.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => {
                setInputValue((prev) => prev + emoji);
                setShowEmojiPicker(false);
              }}
              className="text-xl leading-none"
              style={{ lineHeight: '1.25rem' }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

